import React, { Fragment, useEffect, useState } from "react";
import { Col, Container, Row, Button, Dropdown, Modal } from "react-bootstrap";
import { FaCalendarAlt, FaCheck, FaSync } from "react-icons/fa";
import toast from "react-hot-toast";
import { Shimmer } from "react-shimmer";

import { voucherHeader } from "../../constants/CONSTANT";
import JajotApi from "../../api/JajotApi";
import RecordDataTable from "../RecordDataTable";
import RecordModal from "../common/RecordModal";
import { TopNavigation } from "../layout/TopNavigation";
import { useTallySync } from "../../hooks/useTallySync";
import SyncModal from "../common/SyncModal";
import TaskModal from "../common/TaskModal";

function Voucher() {
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [financialYears, setFinancialYears] = useState([]);
  const [financialYear, setFinancialYear] = useState("");
  const [showTaskModal, setShowTaskModal] = useState(false);
  const { isSyncing, syncStatus, startSync, stopSync } = useTallySync(() => {
    fetchVouchers(financialYear);
  });

  useEffect(() => {
    fetchFinancialYears();
  }, []);

  const fetchFinancialYears = async () => {
    try {
      const result = await JajotApi.getFinancialYears();
      if (!result?.success) throw new Error("Failed to fetch financial years");

      const fyOptions = result.records.map((fy) => ({
        label: fy.Name,
        value: fy.Id,
        selected: fy.Current__c,
      }));

      setFinancialYears(fyOptions);

      const currentFY = fyOptions.find((fy) => fy.selected);
      if (currentFY) {
        fetchVouchers(currentFY.value);
        setFinancialYear(currentFY.value);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to determine financial year");
    }
  };

  const fetchVouchers = async (fyId = null) => {
    try {
      setIsLoading(true);
      const result = await JajotApi.getVoucherRecords(fyId);
      if (!result?.success) throw new Error("No vouchers found");
      setRecords(result.records || []);
    } catch (err) {
      console.error(err);
      setRecords([]);
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Fragment>
      <div className="d-flex">
        <TopNavigation active="Vouchers" />
      </div>

      <Container fluid className="py-3">
        <div className="bg-white shadow-sm rounded p-4">
          <Row className="mb-4 align-items-center">
            <Col>
              <h4 className="fw-bold text-primary mb-0">Voucher Records</h4>
              <small className="text-muted">
                Manage and view all voucher entries
              </small>
            </Col>

            <Col xs="auto" className="d-flex align-items-center gap-3">
              <Button
                variant="outline-secondary"
                className="shadow-sm py-2"
                disabled={isLoading || isSyncing}
                onClick={() => startSync("fetch-voucher")}
              >
                <FaSync className="me-2" /> Sync with Tally
              </Button>

              <div className="d-flex align-items-center gap-2 shadow-sm">
                <Dropdown
                  onSelect={(val) => {
                    setFinancialYear(val);
                    fetchVouchers(val);
                  }}
                >
                  <Dropdown.Toggle
                    variant="light"
                    size="sm"
                    className="d-flex align-items-center bg-white text-dark border-secondary p-1"
                    style={{ minWidth: "140px" }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center me-2"
                      style={{ width: "30px" }}
                    >
                      <FaCalendarAlt size={16} className="text-secondary" />
                    </div>
                    <div className="d-flex flex-column">
                      <span className="small text-secondary">
                        Financial Year:
                      </span>
                      <span className="badge bg-success text-white rounded-pill py-0 px-1">
                        {financialYears.find((y) => y.value === financialYear)
                          ?.label || "Select"}
                      </span>
                    </div>
                  </Dropdown.Toggle>

                  <Dropdown.Menu
                    style={{ maxHeight: "250px", overflowY: "auto" }}
                  >
                    {financialYears.map((year) => {
                      const isActive = year.value === financialYear;
                      return (
                        <Dropdown.Item
                          key={year.value}
                          eventKey={year.value}
                          active={isActive}
                          className="d-flex justify-content-between align-items-center"
                          style={{
                            color: isActive ? "#0d6efd" : "",
                            backgroundColor: isActive
                              ? "rgba(13,110,253,0.1)"
                              : "",
                          }}
                        >
                          <span className="d-flex align-items-center gap-2">
                            <FaCalendarAlt className="text-muted" />{" "}
                            {year.label}
                          </span>
                          {isActive && <FaCheck className="text-primary" />}
                        </Dropdown.Item>
                      );
                    })}
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              <Button
                className="fw-bold shadow-sm px-4 py-2"
                onClick={() => setShowTaskModal(true)}
                disabled={isLoading}
              >
                Add Task
              </Button>
            </Col>
          </Row>

          {isLoading ? (
            <div className="p-3">
              <Shimmer width={600} height={30} className="mb-3" />
              <Shimmer width={-1} height={20} className="mb-2" />
              <Shimmer width={-1} height={20} className="mb-2" />
              <Shimmer width={-1} height={20} />
            </div>
          ) : records.length > 0 ? (
            <RecordDataTable
              tableHeaders={voucherHeader}
              tableData={records}
              handleRowClick={setSelectedRecord}
            />
          ) : (
            <div className="text-center text-muted py-5">
              <h6>No voucher records found</h6>
            </div>
          )}
        </div>
      </Container>

      {selectedRecord && (
        <RecordModal
          show={!!selectedRecord}
          onHide={() => setSelectedRecord(null)}
          record={selectedRecord}
          header={voucherHeader}
          defaultType="vouchers"
        />
      )}
      <SyncModal
        show={isSyncing}
        title="Syncing Voucher Records..."
        status={syncStatus}
        onHide={stopSync}
      />

      {showTaskModal && (
        <TaskModal
          show={showTaskModal}
          onHide={() => setShowTaskModal(false)}
          records={records}
          header="Voucher"
        />
      )}
    </Fragment>
  );
}

export default Voucher;
